import { useEffect, useMemo, useState } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import TableComponent, { DataTypeProps } from "../../components/TableComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SearchComponent from "../../components/SearchComponent";
import SelectComponent from "../../components/SelectComponent";
import { createColumn } from "../../utils/tableUtils";
import { tableDateFormatter } from "../../utils/formatDate";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay,
} from "../../components/styled/common";
import { useKvkkContext } from "../../context/KvkkContext/KvkkContext";
import {
	getKvkkAppLabel,
	getKvkkPlatformLabel,
	KVKK_APP_SELECT_OPTIONS,
	KVKK_LIST_PATH,
	KVKK_PLATFORM_SELECT_OPTIONS,
	KvkkListItem,
} from "../../context/KvkkContext/constants";

// Older records have no title; the table shows "-" for them and the delete question names the record by its kind.
type KvkkRow = KvkkListItem & { titleText: string; platformText: string; appText: string };

const formatDate = (date: string | null) => (date ? tableDateFormatter(date, "DD.MM.YYYY") : "-");

const getSwitchText = (value: boolean) => (value ? "Açık" : "Kapalı");

const getRecordName = (row: KvkkRow) => {
	const title = row.title?.trim();
	if (title) {
		return `"${title}"`;
	}
	const kind = row.isPage ? "sayfa" : "onay";
	return `${row.platformText} - ${row.appText} ${kind} metni`;
};

const KvkkList: React.FC = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<KvkkRow | null>(null);
	const { kvkkList, clearKvkkList, clearKvkkFormData, getKvkkList, removeKvkk, operationStatus } = useKvkkContext();

	const [searchText, setSearchText] = useState("");
	const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
	const [selectedAppId, setSelectedAppId] = useState<string | null>(null);

	const navigate = useNavigate();

	const rows = useMemo<KvkkRow[]>(
		() => kvkkList.map(item => ({
			...item,
			titleText: item.title?.trim() || "-",
			platformText: getKvkkPlatformLabel(item.platform),
			appText: getKvkkAppLabel(item.appId),
		})),
		[kvkkList]
	);

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return rows.filter(row =>
			(!search || (row.title ?? "").toLocaleLowerCase("tr-TR").includes(search))
			&& (!selectedPlatform || String(row.platform) === selectedPlatform)
			&& (!selectedAppId || String(row.appId) === selectedAppId)
		);
	}, [rows, searchText, selectedPlatform, selectedAppId]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";

	useEffect(() => {
		clearKvkkList();
		clearKvkkFormData();
		getKvkkList();

		return () => {
			clearKvkkList();
		};
	}, [clearKvkkList, clearKvkkFormData, getKvkkList]);

	// The title takes the free width; the other columns keep the width their headers need (measured: text, sorter and
	// padding). Like the other lists only the date the record was added is shown, so the title keeps room at 1280px.
	const columns = useMemo(() => [
		createColumn({
			title: "Başlık",
			dataIndex: "titleText",
			sorterType: "string",
		}),
		createColumn({
			title: "Platform",
			dataIndex: "platformText",
			width: 100,
			sorterType: "string",
		}),
		createColumn({
			title: "Uygulama Adı",
			dataIndex: "appText",
			width: 136,
			sorterType: "string",
		}),
		createColumn({
			title: "Sayfa Metni",
			dataIndex: "isPage",
			width: 120,
			sorterType: "boolean",
			render: (text, record) => getSwitchText(record.isPage),
		}),
		createColumn({
			title: "Yönetici Metni",
			dataIndex: "isManager",
			width: 140,
			sorterType: "boolean",
			render: (text, record) => getSwitchText(record.isManager),
		}),
		createColumn({
			title: "Eklenme Tarihi",
			dataIndex: "creationDate",
			width: 144,
			sorterType: "isoDate",
			render: (text, record) => formatDate(record.creationDate),
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 50,
			render: (text, record) => {
				const item = record as KvkkRow;
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(`${KVKK_LIST_PATH}/edit/${item.kvkkTextId}`),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(item);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate]);

	return (
		<div>
			<HeaderComponent>KVKK Metinleri</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Başlık Ara"
								style={{ width: "16vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
							<SelectComponent
								allowClear
								placeholder="Platform Seç"
								style={{ width: "9.375vw" }}
								options={KVKK_PLATFORM_SELECT_OPTIONS}
								value={selectedPlatform}
								onChange={value => setSelectedPlatform(value ?? null)}
							/>
							<SelectComponent
								allowClear
								placeholder="Uygulama Seç"
								style={{ width: "9.375vw" }}
								options={KVKK_APP_SELECT_OPTIONS}
								value={selectedAppId}
								onChange={value => setSelectedAppId(value ?? null)}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate(`${KVKK_LIST_PATH}/add`)}
							>
								Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!isFetching && (
					<TableComponent
						columns={columns}
						dataSource={tableData as unknown as DataTypeProps[]}
						rowKey="kvkkTextId"
					/>
				)}
			</Container>
			<Modal
				title="KVKK Metnini Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button
						loading={isDeleting}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							if (currentItem) {
								await removeKvkk(currentItem.kvkkTextId);
							}
							setIsModalVisible(false);
						}}
					>
						Evet
					</Button>,
				]}
			>
				<p>
					<strong>{currentItem ? getRecordName(currentItem) : ""}</strong> metnini silmek istediğinizden emin misiniz?
					Bu metni gösteren sayfa ve pencereler artık metin gösteremez.
				</p>
			</Modal>
		</div>
	);
};

export default KvkkList;
